import React, { useState } from 'react';
import { Box, Typography, TextField, MenuItem, Button, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUserStats } from '../hooks/useUserStats';

const activityLevels = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
  { value: 'athlete', label: 'Athlete' },
];

const sexOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

interface UserStatsPageProps {
  onBack?: () => void;
}

const UserStatsPage: React.FC<UserStatsPageProps> = ({ onBack }) => {
  const [sex, setSex] = useState('');
  const [height, setHeight] = useState('');
  const [activity, setActivity] = useState('');
  const [goalWeight, setGoalWeight] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { saveUserStats, loading, error } = useUserStats();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await saveUserStats({
      sex,
      height: Number(height),
      activity,
      goalWeight: Number(goalWeight),
    });
    if (success) {
      setSubmitted(true);
      setTimeout(() => {
        setSex('');
        setHeight('');
        setActivity('');
        setGoalWeight('');
        setSubmitted(false);
      }, 1500);
    }
  };

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', mt: 8, p: 3, bgcolor: '#fff', borderRadius: 3, boxShadow: 2, position: 'relative' }}>
      {onBack && (
        <Button
          onClick={onBack}
          variant="text"
          size="small"
          sx={{ position: 'absolute', top: 8, left: 8, minWidth: 0, p: 1 }}
        >
            <CloseIcon sx={{ color: '#222', fontSize: 32 }} />
        </Button>
      )}
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, mt: 4, textAlign: 'center' }}>
        User Stats
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              label="Sex"
              value={sex}
              onChange={e => setSex(e.target.value)}
              fullWidth
              required
            >
              {sexOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Height (cm)"
              type="number"
              value={height}
              onChange={e => setHeight(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Daily Activity Level"
              value={activity}
              onChange={e => setActivity(e.target.value)}
              fullWidth
              required
            >
              {activityLevels.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Goal Weight (lbs)"
              type="number"
              value={goalWeight}
              onChange={e => setGoalWeight(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || submitted}
              sx={{ py: 1.2, fontSize: '1.1rem', borderRadius: 8, backgroundColor: '#00A3FF', '&:hover': { backgroundColor: '#0091E6' } }}
            >
              {submitted ? 'Submitted!' : loading ? 'Saving...' : 'Submit'}
            </Button>
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Typography color="error" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      </form>
    </Box>
  );
};

export default UserStatsPage;
